import { useState, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { karnatakaCities, regionFilters } from "@/mocks/karnatakaCities";
import { landListings } from "@/mocks/landListings";
import { usePageContent } from "@/hooks/usePageContent";
import { useSettings } from "@/context/SettingsContext";
import { usePageMeta } from "@/hooks/usePageMeta";

function stripHtml(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}

const dedicatedCitySlugs: Record<string, string> = {
  bengaluru: "bengaluru",
  mysuru: "mysuru",
  mangaluru: "mangaluru",
};

function getCityLink(cityId: string, cityName: string) {
  if (dedicatedCitySlugs[cityId]) {
    return { to: `/karnataka-cities/${dedicatedCitySlugs[cityId]}`, label: "Explore City" };
  }
  return { to: `/listings?city=${encodeURIComponent(cityName)}`, label: "View Listings" };
}

const featuredListings = landListings.slice(0, 6);

const landGuideItems = [
  {
    icon: "ri-map-pin-2-line",
    title: "City Selection",
    description: "Understand which Karnataka city aligns with your industry and budget requirements.",
  },
  {
    icon: "ri-file-list-3-line",
    title: "Zoning & Permits",
    description: "Navigate local zoning laws, KIADB approvals, and state-level clearances.",
  },
  {
    icon: "ri-money-rupee-circle-line",
    title: "Rent Benchmarks",
    description: "Compare average rent ranges across all 12 major Karnataka cities.",
  },
  {
    icon: "ri-truck-line",
    title: "Logistics Access",
    description: "Evaluate highway, railway, and port connectivity for each region.",
  },
];

// ── City name variants for matching location strings ──
const CITY_ALIASES: Record<string, string[]> = {
  Bengaluru: ["Bengaluru", "Bangalore", "Bengaluru Rural", "North Bengaluru", "Bengaluru East"],
  Mysuru: ["Mysuru", "Mysore"],
  Hubli: ["Hubli", "Hubballi"],
  Mangaluru: ["Mangaluru", "Mangalore"],
  Belagavi: ["Belagavi", "Belgaum"],
  Ballari: ["Ballari", "Bellary"],
  Kalaburagi: ["Kalaburagi", "Gulbarga"],
  Shivamogga: ["Shivamogga", "Shimoga"],
  Udupi: ["Udupi"],
  Tumakuru: ["Tumakuru", "Tumkur"],
  Vijayapura: ["Vijayapura", "Bijapur"],
  Davanagere: ["Davanagere", "Davangere"],
};

function matchCityToLocation(cityName: string, location: string): boolean {
  const aliases = CITY_ALIASES[cityName] || [cityName];
  const loc = location.toLowerCase();
  return aliases.some((alias) => loc.includes(alias.toLowerCase()));
}

// ── Build "recently added" listings per city ──
function buildRecentByCity() {
  const result: { city: (typeof karnatakaCities)[0]; listings: typeof landListings }[] = [];
  for (const city of karnatakaCities) {
    const matched = landListings.filter((l) => matchCityToLocation(city.name, l.location));
    if (matched.length > 0) {
      result.push({ city, listings: matched.slice(0, 3) });
    }
  }
  return result;
}

const recentByCity = buildRecentByCity();

export default function KarnatakaCitiesPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeRegion, setActiveRegion] = useState("All");
  const { getContent } = usePageContent('karnataka-cities');
  const { settings } = useSettings();

  usePageMeta({
    title: "Land for Sale, Lease & Rent Across Karnataka Cities | CashMyProperty.ai",
    description: "Explore verified land listings in 12+ Karnataka cities — Bengaluru, Mysuru, Hubli, Mangaluru and more. Compare average rent ranges, zoning, logistics and investment potential across every region.",
    canonicalUrl: "https://cashmyproperty.ai/karnataka-cities",
  });

  const totalListings = karnatakaCities.reduce((sum, c) => sum + c.listingCount, 0);

  const heroBadge = stripHtml(getContent('kc-hero-badge')) || `${totalListings}+ Cities Covered`;
  const heroSub = stripHtml(getContent('kc-hero-sub')) || 'From Bengaluru industrial corridors to coastal plots in Udupi, discover verified land listings in every region of Karnataka. Filter by city, region, or land type.';
  const citiesHeading = stripHtml(getContent('kc-cities-heading')) || 'Cities &amp; Regions';
  const recentHeading = stripHtml(getContent('kc-recent-heading')) || 'Recently Added';
  const featuredHeading = stripHtml(getContent('kc-featured-heading')) || 'Featured Karnataka Listings';
  const guideHeading = stripHtml(getContent('kc-guide-heading')) || 'Everything You Need to Know';
  const ctaHeading = stripHtml(getContent('kc-cta-heading')) || 'Have Land in Karnataka?';
  const ctaSub = stripHtml(getContent('kc-cta-sub')) || 'List your property on CashMyProperty.ai and reach thousands of verified tenants across the state. Free listing, zero upfront fees.';

  const filteredCities = useMemo(() => {
    return karnatakaCities.filter((city) => {
      const matchesSearch =
        searchQuery === "" ||
        city.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        city.region.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRegion = activeRegion === "All" || city.region === activeRegion;
      return matchesSearch && matchesRegion;
    });
  }, [searchQuery, activeRegion]);

  // Redirect to home when the Karnataka Cities page is disabled in dashboard settings
  if (settings.features.karnataka_cities_page === false) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageLayout>
      {/* ── Hero ── */}
      <section className="relative w-full min-h-[480px] md:min-h-[520px] overflow-hidden bg-background-950 flex items-center">
        <img
          src="https://readdy.ai/api/search-image?query=Aerial%20panoramic%20view%20of%20Karnataka%20state%20landscape%20with%20green%20hills%2C%20farmland%2C%20and%20modern%20cities%20in%20distance%2C%20golden%20warm%20sunrise%20light%2C%20dramatic%20clouds%2C%20India%20south%20Indian%20terrain%2C%20cinematic%20wide%20angle%20photography%2C%20warm%20amber%20and%20teal%20tones%2C%20highly%20detailed%2C%20no%20text&width=1800&height=800&seq=karnataka-hero-01&orientation=landscape&nocache=true"
          alt="Karnataka Land Opportunities"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background-950" />
        <div className="relative container mx-auto px-4 lg:px-8 py-20 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary-500/40 bg-primary-500/10 mb-6">
            <span className="text-primary-400 text-xs font-semibold uppercase tracking-widest">
              {totalListings}+ Cities Covered
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-[1.1] mb-5 max-w-3xl mx-auto">
            <span className="text-white [text-shadow:0_0_24px_rgba(0,0,0,0.92)]">
              Land Opportunities Across{" "}
            </span>
            <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent drop-shadow-[0_0_16px_rgba(0,0,0,0.78)]">
              Every Karnataka City
            </span>
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed [text-shadow:0_0_16px_rgba(0,0,0,0.84)]">
            {heroSub}
          </p>
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <div className="w-5 h-5 flex items-center justify-center absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400">
                <i className="ri-search-line text-lg" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for a city..."
                className="w-full pl-12 pr-5 py-4 rounded-xl bg-background-50/95 border border-primary-700/30 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/50 focus:ring-2 focus:ring-primary-500/20 transition-all"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Cities & Regions ── */}
      <section className="py-16 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950">
              {citiesHeading}
            </h2>
            <p className="text-sm text-foreground-500">
              {filteredCities.length} cities
            </p>
          </div>

          {/* Region filters */}
          <div className="flex flex-wrap gap-2 mb-8">
            {regionFilters.map((f) => (
              <button
                key={f.value}
                onClick={() => setActiveRegion(f.value)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeRegion === f.value
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:text-foreground-950 hover:bg-background-200 border border-primary-800/20"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* City cards grid */}
          {filteredCities.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-background-50 border border-primary-500/30 mx-auto mb-4">
                <i className="ri-search-line text-2xl text-primary-500" />
              </div>
              <h3 className="text-lg font-display font-semibold text-foreground-950 mb-2">
                No cities found
              </h3>
              <p className="text-sm text-foreground-500">
                Try a different search term or region filter.
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredCities.map((city) => (
                <div
                  key={city.id}
                  className="group rounded-2xl card-dark overflow-hidden hover:border-primary-500/50 transition-all duration-300"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={city.image}
                      alt={city.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3">
                      <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-background-950/60 backdrop-blur-sm text-background-50">
                        {city.listingCount} listings
                      </span>
                    </div>
                    <div className="absolute top-3 left-3">
                      <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500/80 backdrop-blur-sm text-background-50">
                        {city.region}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-semibold text-lg text-foreground-950 mb-1.5 group-hover:text-primary-500 transition-colors">
                      {city.name}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">
                      {city.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {city.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-block px-2 py-0.5 rounded-md text-xs bg-secondary-100 text-secondary-800 border border-secondary-200"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-primary-900/20">
                      <div>
                        <span className="text-[11px] text-foreground-400 uppercase tracking-wider">
                          Avg. Rent Range
                        </span>
                        <p className="text-sm font-semibold text-foreground-950">
                          {city.avgRentRange}
                        </p>
                      </div>
                      <Link
                        to={getCityLink(city.id, city.name).to}
                        className="inline-flex items-center gap-1 text-sm text-primary-500 font-medium hover:text-primary-400 transition-colors whitespace-nowrap"
                      >
                        {getCityLink(city.id, city.name).label}
                        <i className="ri-arrow-right-line" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Recently Added by City ── */}
      {recentByCity.length > 0 && (
        <section className="py-16 bg-background-100">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-1">
                  {recentHeading}
                </h2>
                <p className="text-sm text-foreground-500">
                  Newest land listings across Karnataka cities
                </p>
              </div>
            </div>

            {recentByCity.map((group) => (
              <div key={group.city.id} className="mb-10 last:mb-0">
                <div className="flex items-center gap-3 mb-5">
                  <h3 className="text-lg font-display font-semibold text-foreground-950">
                    {group.city.name}
                  </h3>
                  <span className="text-xs text-foreground-500 bg-background-200/60 px-2.5 py-1 rounded-full">
                    {group.listings.length} {group.listings.length === 1 ? "listing" : "listings"}
                  </span>
                  <Link
                    to={getCityLink(group.city.id, group.city.name).to}
                    className="ml-auto inline-flex items-center gap-1 text-sm text-primary-500 font-medium hover:text-primary-400 transition-colors whitespace-nowrap"
                  >
                    View all in {group.city.name}
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {group.listings.map((listing) => (
                    <div
                      key={listing.id}
                      className="group rounded-2xl card-dark overflow-hidden hover:border-primary-500/50 transition-all duration-300 flex flex-col"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img
                          src={listing.image}
                          alt={listing.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${listing.statusColor}`}>
                            {listing.status}
                          </span>
                        </div>
                        <div className="absolute top-3 right-3">
                          <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-accent-500/80 backdrop-blur-sm text-background-50">
                            New
                          </span>
                        </div>
                      </div>
                      <div className="p-4 flex flex-col flex-1">
                        <div className="flex items-center gap-2 text-xs text-foreground-500 mb-1.5">
                          <i className="ri-map-pin-line text-primary-500" />
                          {listing.location}
                        </div>
                        <h4 className="font-display font-semibold text-sm text-foreground-950 mb-1.5 group-hover:text-primary-500 transition-colors leading-snug">
                          {listing.title}
                        </h4>
                        <p className="text-xs text-foreground-600 leading-relaxed mb-3 line-clamp-2">
                          {listing.description}
                        </p>
                        <div className="flex items-center justify-between mt-auto pt-3 border-t border-primary-900/20">
                          <span className="text-sm font-semibold text-primary-600">
                            {listing.expectedRent}
                          </span>
                          <span className="text-xs text-foreground-400">{listing.postedDate}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Featured Karnataka Listings ── */}
      <section className="py-16 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-1">
                {featuredHeading}
              </h2>
              <p className="text-sm text-foreground-500">
                Hand-picked verified properties across the state
              </p>
            </div>
            <Link
              to="/listings"
              className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-500 font-medium hover:text-primary-400 transition-colors whitespace-nowrap"
            >
              View All
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredListings.map((listing) => (
              <div
                key={listing.id}
                className="group rounded-2xl card-dark overflow-hidden hover:border-primary-500/50 transition-all duration-300 flex flex-col"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={listing.image}
                    alt={listing.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50">
                      Featured
                    </span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-background-950/60 backdrop-blur-sm text-background-50">
                      {listing.category}
                    </span>
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2 text-xs text-foreground-500 mb-2">
                    <i className="ri-map-pin-line text-primary-500" />
                    {listing.location}
                  </div>
                  <h3 className="font-display font-semibold text-base text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors leading-snug">
                    {listing.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3 flex-1 line-clamp-2">
                    {listing.description}
                  </p>
                  <div className="flex items-center justify-between pt-3 border-t border-primary-900/20">
                    <div>
                      <p className="text-sm font-semibold text-primary-600">
                        {listing.expectedRent}
                      </p>
                      <p className="text-xs text-foreground-400">{listing.size}</p>
                    </div>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-1 text-xs text-primary-500 font-medium hover:text-primary-400 transition-colors whitespace-nowrap"
                    >
                      Details
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 text-center sm:hidden">
            <Link
              to="/listings"
              className="inline-flex items-center gap-1 text-sm text-primary-500 font-medium hover:text-primary-400 transition-colors whitespace-nowrap"
            >
              View All Listings
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Karnataka Land Guide ── */}
      <section className="py-16 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Karnataka Land Guide
            </span>
            <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-4">
              {guideHeading}
            </h2>
            <p className="text-foreground-600">
              From zoning to rent benchmarks — our comprehensive guide helps you make informed land decisions across Karnataka.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {landGuideItems.map((item) => (
              <div
                key={item.title}
                className="p-6 rounded-2xl card-dark text-center group hover:border-primary-500/50 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/10 border border-primary-500/20 mx-auto mb-4 group-hover:bg-primary-500/20 transition-colors">
                  <i className={`${item.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-base text-foreground-950 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Region Quick Nav ── */}
      <section className="py-12 bg-background-50 border-t border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-3">
            {regionFilters.slice(1).map((r) => (
              <button
                key={r.value}
                onClick={() => {
                  setActiveRegion(r.value);
                  document
                    .getElementById("cities-section")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="px-4 py-2 rounded-full text-sm font-medium bg-background-100 text-foreground-600 border border-primary-800/20 hover:text-primary-500 hover:border-primary-500/30 transition-all cursor-pointer whitespace-nowrap"
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="py-20 bg-background-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-accent-500/10" />
        <div className="relative container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            {ctaHeading}
          </h2>
          <p className="text-white/70 mb-8 leading-relaxed">
            {ctaSub}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/submit-property"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-map-pin-add-line" />
              List Your Land Free
            </Link>
            <Link
              to="/listings"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-500/40 text-primary-400 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap"
            >
              <i className="ri-search-eye-line" />
              Browse All Karnataka
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}